import { useCallback, useMemo } from 'react'

// project import
import {
  useGetHistoryPricesQuery,
  useGetMissingCurrenciesQuery,
  useGetPricesQuery,
  useGetInstrumentsPricesQuery,
  useGetInstrumentsHistoryPricesQuery,
  // useGetHPricesQuery,
  // useGetRTPricesQuery,
} from 'store/api/merklebaseAPI'
import {
  selectParsedPrices,
  selectParsedPricesByCurrency,
  selectInstrumentsPricesByInstrument,
  selectParsedInstrumentsPrices,
  selectPricesLoading,
  selectPricesFetching,
  selectInstrumentsPricesLoading,
  selectInstrumentsPricesFetching,
} from 'store/selectors/prices'
import { useSelector } from 'react-redux'
import { mapPrices, mapInstrumentsPrices } from 'utils/mappers/prices'
import dayjs from 'dayjs'

// ----------------------------------------------------------------------

export default function usePrices({ timestamp } = {}) {
  // Ensure fetch runs (subscribes to cache) - no-arg queries so we keep a single cache key
  useGetPricesQuery(undefined, {
    pollingInterval: 1000 * 60,
  })
  useGetInstrumentsPricesQuery(undefined, {
    pollingInterval: 1000 * 60,
  })

  // Use selectors for derived data - reduces re-renders
  const parsedPrices = useSelector(selectParsedPrices)
  const pricesByCurrency = useSelector(selectParsedPricesByCurrency)
  const instrumentsPricesByInstrument = useSelector(selectInstrumentsPricesByInstrument)
  const isPricesLoading = useSelector(selectPricesLoading)
  const isPricesFetching = useSelector(selectPricesFetching)
  const instrumentsPricesLoading = useSelector(selectInstrumentsPricesLoading)
  const instrumentsPricesFetching = useSelector(selectInstrumentsPricesFetching)

  const {
    data: historyPrices = null,
    isLoading: isHistoryPricesLoading,
  } = useGetHistoryPricesQuery({
    timestamp: timestamp || dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
  })

  // const {
  //   data: hPrices = null,
  //   isLoading: isHPricesLoading,
  //   isFetching: isHPricesFetching,
  // } = useGetHPricesQuery({})

  // const {
  //   data: rtPrices = null,
  //   isLoading: isRTPricesLoading,
  //   isFetching: isRTPricesFetching,
  // } = useGetRTPricesQuery(
  //   {},
  //   {
  //     pollingInterval: 1000 * 6 * 4,
  //   }
  // )

  const {
    data: missingCurrencies,
    isLoading: isMissingCurrenciesLoading,
    isFetching: isMissingCurrenciesFetching,
  } = useGetMissingCurrenciesQuery()

  const {
    data: instrumentsHistoryPrices,
    isLoading: instrumentsHistoryPricesLoading,
    isFetching: instrumentsHistoryPricesFetching,
  } = useGetInstrumentsHistoryPricesQuery({
    timestamp: timestamp || dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
  })

  const parsedInstrumentsPrices = useSelector(selectParsedInstrumentsPrices)
  const parsedInstrumentsHistoryPrices = useMemo(
    () => instrumentsHistoryPrices?.map(mapInstrumentsPrices),
    [instrumentsHistoryPrices]
  )

  const parsedHistoryPrices = useMemo(
    () => historyPrices?.prices?.map(mapPrices),
    [historyPrices?.prices]
  )

  const sumInstrumentsToInstrument = useCallback(
    (instruments, instrument) => {
      const sumUSD = instruments?.reduce((acc, { instrument, amount }) => {
        const price = pricesByCurrency?.[instrument]?.currentValue || 0
        return acc + price * amount
      }, 0)

      return pricesByCurrency?.[instrument]?.currentValue
        ? sumUSD / pricesByCurrency[instrument].currentValue
        : 0
    },
    [pricesByCurrency]
  )

  return {
    prices: { data: parsedPrices || [], byCurrency: pricesByCurrency || {} },
    historyPrices: {
      data: parsedHistoryPrices || [],
    },
    missingCurrencies: {
      data: missingCurrencies || [],
      isLoading: isMissingCurrenciesLoading || isMissingCurrenciesFetching,
    },
    instrumentsPrices: {
      data: parsedInstrumentsPrices || [],
      byInstrument: instrumentsPricesByInstrument,
      // true during initial load and any refetch
      isLoading: instrumentsPricesLoading || instrumentsPricesFetching,
    },
    instrumentsHistoryPrices: {
      data: parsedInstrumentsHistoryPrices || [],
      isLoading: instrumentsHistoryPricesLoading || instrumentsHistoryPricesFetching,
    },
    sumInstrumentsToInstrument,
    // Combined loading: includes refetching via *Fetching flags
    isLoading:
      isPricesLoading ||
      isPricesFetching ||
      isHistoryPricesLoading ||
      instrumentsPricesLoading ||
      instrumentsPricesFetching,
    // hPrices: { data: hPrices || [], isLoading: isHPricesLoading || isHPricesFetching },
    // rtPrices: {
    //   data: rtPrices || [],
    //   byCurrency: realTimePicesByCurrency,
    //   isLoading: isRTPricesLoading || isRTPricesFetching,
    // },
    // Indicates whether any real-time prices query is actively fetching
    isRealTimePricesFetching:
      isPricesLoading || isPricesFetching || instrumentsPricesLoading || instrumentsPricesFetching,
  }
}
